import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight, Search } from "lucide-react";
import { MaskLines } from "@/components/site/Motion";
import { useFetch } from "@/hooks/useFetch";

const ease = [0.22, 1, 0.36, 1];

const Frame = ({ src, className, style, label }) => (
  <motion.div style={style} className={`absolute overflow-hidden rounded-[1.4rem] border border-white/10 bg-ink-2 shadow-[0_40px_120px_-30px_rgba(0,0,0,0.9)] ${className}`}>
    <img src={src} alt="" className="h-full w-full object-cover" />
    {label && (
      <div className="absolute inset-3 vf-corners">
        <span className="absolute left-3 top-3 flex items-center gap-1.5 font-mono text-[11px] text-white">
          <span className="rec-dot h-2 w-2 rounded-full bg-flash" /> REC
        </span>
        <span className="absolute bottom-3 right-3 font-mono text-[11px] text-white/90">{label}</span>
      </div>
    )}
  </motion.div>
);

const HeroStack = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBack = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const yFront = useTransform(scrollYProgress, [0, 1], [0, 60]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }} className="relative h-[420px] sm:h-[520px] lg:h-[600px]" style={{ perspective: 1200 }} data-testid="hero-photo-stack">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative h-full w-full"
        initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, ease, delay: 0.3 }}>
        <Frame src="/img/sport.jpg" className="left-0 top-[6%] h-[46%] w-[46%] -rotate-6 opacity-90" style={{ y: yBack, translateZ: -60 }} />
        <Frame src="/img/corp.jpg" className="right-0 top-0 h-[40%] w-[42%] rotate-6 opacity-90" style={{ y: yBack, translateZ: -40 }} />
        <Frame src="/img/dance.jpg" className="left-[12%] top-[20%] h-[70%] w-[76%]" style={{ y: yFront, translateZ: 60 }} label="ISO 3200 · 1/250 · f2.8" />
        <motion.div style={{ y: yFront, translateZ: 120 }} className="absolute bottom-[2%] left-[2%] rounded-2xl border border-white/15 bg-ink/80 px-4 py-3 backdrop-blur-xl sm:left-[6%]">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-flash">Foto encontrada</p>
          <p className="mt-1 font-display text-lg font-bold">Você está aqui ↗</p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const { data: stats } = useFetch("/stats", {});

  return (
    <section className="grain relative overflow-hidden pt-[110px]" data-testid="hero-section">
      <div aria-hidden className="pointer-events-none absolute -left-40 top-20 h-[520px] w-[520px] rounded-full bg-violet/30 blur-[140px]" />
      <div aria-hidden className="pointer-events-none absolute -right-20 bottom-0 h-[420px] w-[420px] rounded-full bg-flash/20 blur-[140px]" />
      <div className="relative mx-auto grid max-w-[1400px] items-center gap-10 px-5 pb-16 sm:px-8 lg:grid-cols-12 lg:pb-24">
        <div className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}
            className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-haze">
            <span className="rec-dot h-2 w-2 rounded-full bg-flash" /> Fotografia de eventos · cobertura sem custo
          </motion.p>
          <h1 className="mt-6 font-display text-[4.2rem] font-extrabold leading-[0.88] tracking-[-0.04em] sm:text-[7rem] lg:text-[9rem]" data-testid="hero-title">
            <MaskLines lines={["Eu te vi", <span key="la" className="text-flash">lá<span className="text-violet-lilac">.</span></span>]} delay={0.15} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease, delay: 0.6 }}
            className="mt-8 max-w-xl text-base text-haze md:text-lg">
            Você vive o evento, a gente fotografa tudo. Depois é só encontrar suas fotos, pagar antecipado e baixar em alta — sem marca d'água.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease, delay: 0.75 }}
            className="mt-9 flex flex-wrap gap-3">
            <Link to="/eventos" data-testid="hero-find-photos-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-flash px-7 py-4 font-semibold text-ink transition-[background-color,transform] duration-300 hover:scale-[1.03] hover:bg-flash-2">
              Encontrar minhas fotos <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:rotate-45" />
            </Link>
            <Link to="/fotografos" data-testid="hero-hire-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-4 font-semibold transition-[border-color,background-color] duration-300 hover:border-violet-lilac hover:bg-white/5">
              Contratar fotógrafo
            </Link>
          </motion.div>
          <motion.form initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 0.9 }}
            onSubmit={(e) => { e.preventDefault(); nav(`/eventos?q=${encodeURIComponent(q)}`); }}
            className="mt-8 flex max-w-md items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] p-1.5 pl-5 backdrop-blur-xl">
            <Search className="h-4 w-4 text-haze" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome do evento ou local" data-testid="hero-search-input"
              className="w-full bg-transparent text-sm outline-none placeholder:text-haze/80" />
            <button type="submit" data-testid="hero-search-btn" className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-ink transition-colors hover:bg-flash">Buscar</button>
          </motion.form>
        </div>
        <div className="lg:col-span-5">
          <HeroStack />
        </div>
      </div>
      <div className="relative border-y border-white/10 bg-ink-2/60">
        <div className="mx-auto grid max-w-[1400px] grid-cols-3 divide-x divide-white/10 px-5 sm:px-8">
          {[["events", "eventos publicados"], ["photos", "fotos na plataforma"], ["photographers", "fotógrafos associados"]].map(([k, l]) => (
            <div key={k} className="py-6 pr-3 first:pl-0 [&:not(:first-child)]:pl-4 sm:[&:not(:first-child)]:pl-8">
              <p className="font-display text-3xl font-bold sm:text-5xl" data-testid={`hero-stat-${k}`}>{stats?.[k] ?? 0}</p>
              <p className="mt-1 text-xs text-haze sm:text-sm">{l}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
